/* HidroVerse SalesFlow — contact link helpers
 *
 * Per company policy:
 *   • "Call" buttons should open WhatsApp (NOT the mobile dialer).
 *   • "Email" buttons should open the user's Gmail compose tab
 *     (the team is on Google Workspace).
 *
 * `phoneToWa(phone)` strips formatting and returns a wa.me URL, or null
 * if there's nothing usable to dial.
 */

const COUNTRY_CODE_DEFAULT = "27"; // South Africa — HidroVerse HQ.

/** Normalise any phone string into the digits-only form wa.me expects.
 *
 * Rules (Wave 6):
 *   • If input starts with `+`, treat as already international — strip the +.
 *   • If input starts with `00`, treat as international (drop the 00).
 *   • If input starts with `0` and length is 10, assume SA local → +27 prefix.
 *   • Otherwise, leave digits as-is (international fallback). This means a
 *     pasted international number without a leading + is not clobbered.
 */
/** Canonical phone normaliser — MUST mirror /app/backend/lib/phone.py.
 *
 * Rules (Wave 27.7, Dirk 2026-02-20):
 *   • Strip non-digits.
 *   • Handle the SA double-prefix typo `+270XXXXXXXXX` (12 digits, starts 270)
 *     by stripping the redundant 0 → 27XXXXXXXXX.
 *   • `27XXXXXXXXX` (11 digits)            → pass through
 *   • `0XXXXXXXXX` (10 digits, leading 0)  → swap to `27XXXXXXXXX`
 *   • `XXXXXXXXX` (9 digits, no leading 0) → prefix `27`
 *   • Otherwise return what we have — caller validates with the regex.
 *
 * Returns "" for empty input or unparseable garbage.
 */
export function normalisePhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  // Double-prefix typo: 12 digits starting with the country code + a 0.
  if (digits.startsWith(COUNTRY_CODE_DEFAULT + "0") &&
      digits.length === COUNTRY_CODE_DEFAULT.length + 10) {
    return COUNTRY_CODE_DEFAULT + digits.slice(COUNTRY_CODE_DEFAULT.length + 1);
  }
  if (digits.startsWith(COUNTRY_CODE_DEFAULT) && digits.length === 11) return digits;
  if (digits.startsWith("0") && digits.length === 10) {
    return COUNTRY_CODE_DEFAULT + digits.slice(1);
  }
  if (digits.length === 9) return COUNTRY_CODE_DEFAULT + digits;
  return digits;
}

// SA prefix validation — mirrors backend _ZA_ANY_RE.
const _ZA_ANY_RE = /^27[1-8]\d{8}$/;

/** Strict E.164 validator — true iff the input would survive backend acceptance. */
export function isValidPhone(raw) {
  const n = normalisePhone(raw);
  if (!n) return false;
  if (n.length < 8 || n.length > 15) return false;
  if (n.startsWith(COUNTRY_CODE_DEFAULT) && !_ZA_ANY_RE.test(n)) return false;
  return true;
}

/** Display-format an E.164-like number. Pure cosmetic.
 *   "27821234567" → "+27 82 123 4567"
 *   "447700900123" → "+44 7700 900123"
 *   non-SA → "+CC rest" with simple groups.
 */
export function formatPhoneDisplay(raw) {
  const n = normalisePhone(raw);
  if (!n) return "";
  if (n.startsWith("27") && n.length === 11) {
    return `+27 ${n.slice(2, 4)} ${n.slice(4, 7)} ${n.slice(7)}`;
  }
  // Generic: +CC rest, no fancy groupings.
  if (n.length > 7) return `+${n.slice(0, 2)} ${n.slice(2)}`;
  return `+${n}`;
}

/** Live validation result — used by the input field as you type.
 * Returns { state: 'empty'|'valid'|'warn'|'invalid', message }.
 */
export function validatePhone(raw) {
  if (!raw || !String(raw).trim()) return { state: "empty", message: "" };
  // STRICT: matches the backend acceptance rule exactly. If it doesn't
  // pass here, the backend will 422 — so we surface red immediately.
  if (isValidPhone(raw)) {
    const n = normalisePhone(raw);
    if (n.startsWith("27") && n.length === 11) {
      return { state: "valid", message: "Valid SA number" };
    }
    return { state: "valid", message: "Valid international number" };
  }
  return {
    state: "invalid",
    message: "Use 076 901 2331 or +27 76 901 2331.",
  };
}

/** Returns a wa.me URL or null when the input has no digits. */
export function phoneToWa(raw, opts = {}) {
  const num = normalisePhone(raw);
  if (!num) return null;
  const text = opts.text ? `?text=${encodeURIComponent(opts.text)}` : "";
  return `https://wa.me/${num}${text}`;
}

/** WhatsApp call deep-link. Opens an installed WhatsApp app's call screen.
 *  Falls back to wa.me chat when WhatsApp isn't installed (browser will show
 *  "this site can't be reached" and we have no cure for that — the caller
 *  must use the Chat button instead).
 */
export function phoneToWaCall(raw) {
  const num = normalisePhone(raw);
  if (!num) return null;
  // The unofficial deep-link supported on Android/iOS WhatsApp installs.
  return `whatsapp://call?phone=${num}`;
}

/** Carrier dialer link. Always works on mobile; on desktop opens system dialer
 *  if one is installed (FaceTime, Skype, etc.).
 */
export function phoneToTel(raw) {
  const num = normalisePhone(raw);
  if (!num) return null;
  return `tel:+${num}`;
}

/** Build a vCard 3.0 string for a single contact. */
export function buildVCard({ name, phone, email, org, title }) {
  const lines = ["BEGIN:VCARD", "VERSION:3.0"];
  if (name) lines.push(`FN:${name}`);
  if (org) lines.push(`ORG:${org}`);
  if (title) lines.push(`TITLE:${title}`);
  if (phone) {
    const n = normalisePhone(phone);
    if (n) lines.push(`TEL;TYPE=CELL:+${n}`);
  }
  if (email) lines.push(`EMAIL;TYPE=INTERNET:${email}`);
  lines.push(`REV:${new Date().toISOString()}`);
  lines.push("END:VCARD");
  return lines.join("\r\n");
}

/** Trigger a .vcf download in the browser. */
export function downloadVCard(contact) {
  const vcf = buildVCard(contact);
  const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(contact.name || "contact").replace(/[^\w\-]+/g, "_")}.vcf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/** Returns a mailto: URL with optional subject / body / cc / bcc.
 *
 * `mailto:` respects each user's default mail app (Apple Mail, Outlook,
 * Gmail-as-default, Thunderbird, mobile mail, etc.) so it works for both
 * staff and external clients without forcing anyone into Gmail.
 */
export function mailtoUrl({ to, cc, bcc, subject, body } = {}) {
  const params = new URLSearchParams();
  if (cc) params.set("cc", cc);
  if (bcc) params.set("bcc", bcc);
  if (subject) params.set("subject", subject);
  if (body) params.set("body", body);
  const qs = params.toString();
  return `mailto:${to || ""}${qs ? `?${qs}` : ""}`;
}

/** Kept as an alias for any code that explicitly wants Gmail compose.
 *  Most call sites should use `mailtoUrl` instead so users get their own mail app.
 */
export function gmailComposeUrl({ to, cc, bcc, subject, body } = {}) {
  const params = new URLSearchParams();
  params.set("view", "cm");
  params.set("fs", "1");
  if (to) params.set("to", to);
  if (cc) params.set("cc", cc);
  if (bcc) params.set("bcc", bcc);
  if (subject) params.set("su", subject);
  if (body) params.set("body", body);
  return `https://mail.google.com/mail/?${params.toString()}`;
}

